// Iter86 / Phase 3F — Controlled Iron Condor Auto-Scan Panel.
//
// Sibling of BPCSAutoScanPanel (Iter85) and BCCSAutoScanPanel
// (Iter86). Reads the latest in-memory market snapshot from
// `MarketSnapshotContext`, lets the operator pick which snapshot
// symbols to include, then on one explicit click builds Iron
// Condor payloads (both put and call chains required) and POSTs
// them to the existing Iter62 iron condor preview endpoint:
//
//   POST /api/forge/toolbox/scanner/iron-condor-preview
//
// Results render as Wheel-style cards via StrategyResultCard +
// ScannerSpreadMetrics(module="IRON_CONDOR"). Operator validates
// and decides — no execution, no journal write, no persistence.
//
// HARD CONSTRAINTS (asserted by Iter86 tests):
//  * Manual-run only. No `useEffect`. No auto-fire on mount.
//  * No outbound HTTP except the iron-condor preview endpoint
//    above.
//  * Never calls `/forge/candidates`, `/market-scanner/*`,
//    `/forge/toolbox/market-snapshot/build`,
//    `/forge/toolbox/scanner/bpcs-preview`,
//    `/forge/toolbox/scanner/bccs-preview`,
//    `/forge/toolbox/scanner/cfm-shares-preview`,
//    `/forge/toolbox/scanner/cfm-leaps-preview`, broker / account /
//    position / order / journal / admin endpoints.
//  * No `localStorage` / `sessionStorage` / `IndexedDB` / cookies.
//  * No ranking / winner / best / preferred / auto-select language
//    outside safety disclaimers.

import React, { useMemo, useState } from "react";
import axios from "axios";
import {
  ChevronDown, ChevronUp, AlertTriangle, Search, CheckCircle2,
} from "lucide-react";
import { API } from "@/App";
import { useMarketSnapshot } from "@/contexts/MarketSnapshotContext";
import { useWESContext } from "@/contexts/WESContextProvider";
import {
  summarizeWesContext, applyWesToSubpayload,
} from "@/components/wesContextPayloadHelpers";
import WesProvenanceStrip from "@/components/WesProvenanceStrip";
import StrategyResultCard from "@/components/StrategyResultCard";
import { ScannerSpreadMetrics } from "@/components/ScannerResultNormalization";
import SnapshotScanKit from "@/components/SnapshotScanKit";

const IRON_CONDOR_ENDPOINT = "/forge/toolbox/scanner/iron-condor-preview";
const STALE_SECONDS = 5 * 60;
const DEFAULT_MAX_SYMBOLS = 5;
const TID = "iron-condor-auto-scan";

function ageSeconds(iso) {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.round((Date.now() - t) / 1000));
}

const SAFETY_LABELS = [
  { testid: `${TID}-banner-iron-condor-only`, text: "Iron Condor only." },
  { testid: `${TID}-banner-manual`,            text: "Manual scan only." },
  { testid: `${TID}-banner-no-ranking`,        text: "No ranking." },
  { testid: `${TID}-banner-no-winner`,         text: "No winner." },
  { testid: `${TID}-banner-no-execution`,      text: "No trade execution." },
  { testid: `${TID}-banner-no-journal`,        text: "No journal write." },
  { testid: `${TID}-banner-no-persistence`,    text: "No persistence." },
  { testid: `${TID}-banner-no-account-fetch`,  text: "No account / position fetch." },
];

function chainCounts(row) {
  const chain = Array.isArray(row?.chain) ? row.chain : [];
  return {
    puts:  chain.filter(r => r && r.type === "put").length,
    calls: chain.filter(r => r && r.type === "call").length,
  };
}

function buildPerSymbolPayload(row, wesRow, wesMeta) {
  const warnings = [];
  const chain = Array.isArray(row.chain) ? row.chain : [];
  const puts  = chain.filter(r => r && r.type === "put");
  const calls = chain.filter(r => r && r.type === "call");
  const blocked = puts.length === 0 || calls.length === 0;
  if (blocked) {
    warnings.push("Missing put/call chain — Iron Condor unavailable.");
  }

  const sub = {
    spot_price: row.spot_price ?? null,
    put_chain: puts,
    call_chain: calls,
    wes: {},
  };
  // Iter92 — helper emits the existing
  //   "WES context unavailable — Iron Condor scan may be structurally incomplete."
  // copy if the envelope has no row for this symbol.
  const wesWarnings = applyWesToSubpayload(
    sub, wesRow, wesMeta, "Iron Condor"
  );
  wesWarnings.forEach(w => warnings.push(w));
  if (row.ivr != null) sub.ivr = row.ivr;

  sub.snapshot_meta = {
    snapshot_id: row.__snapshot_id || null,
    snapshot_as_of: row.__snapshot_as_of || null,
    source: row.source || null,
    chosen_expiration: row.chosen_expiration || null,
    dte: row.dte ?? null,
    freshness_age_seconds: ageSeconds(row.__snapshot_as_of),
    warnings,
  };
  return { payload: sub, warnings, blocked };
}

export default function IronCondorAutoScanPanel() {
  const { snapshot } = useMarketSnapshot();
  const { wesContext } = useWESContext();
  const wesSummary = summarizeWesContext(wesContext);
  const wesMeta = {
    wesAsOf: wesSummary.asOf,
    fingerprint: wesSummary.fingerprint,
    version: wesSummary.version,
    stale: wesSummary.stale,
  };
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState({});
  const [maxSymbols, setMaxSymbols] = useState(String(DEFAULT_MAX_SYMBOLS));
  const [minIvr, setMinIvr] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [result, setResult] = useState(null);
  const [lastRequest, setLastRequest] = useState(null);
  const [lastScannedAt, setLastScannedAt] = useState(null);
  const [perSymbolWarnings, setPerSymbolWarnings] = useState({});

  const rows = useMemo(() => {
    if (!snapshot || !Array.isArray(snapshot.rows)) return [];
    return snapshot.rows.filter(r => r && r.symbol);
  }, [snapshot]);

  const seedKey = snapshot?.snapshot_id || "";
  const [seededFor, setSeededFor] = useState("");
  if (seedKey && seedKey !== seededFor) {
    const next = {};
    rows.forEach(r => { next[r.symbol] = true; });
    setSelected(next);
    setSeededFor(seedKey);
  }

  function toggleSymbol(sym) { setSelected(p => ({ ...p, [sym]: !p[sym] })); }
  function selectAll() {
    const next = {}; rows.forEach(r => { next[r.symbol] = true; });
    setSelected(next);
  }
  function clearAll() { setSelected({}); }

  function chainAvailability(row) {
    const { puts, calls } = chainCounts(row);
    return { ok: puts > 0 && calls > 0, puts, calls };
  }

  function buildEnvelope() {
    const maxN = Number(maxSymbols);
    const cap = Number.isFinite(maxN) && maxN > 0
      ? Math.floor(maxN) : DEFAULT_MAX_SYMBOLS;
    const minIvrNum = minIvr.trim() === "" ? null : Number(minIvr);
    const picked = rows
      .filter(r => selected[r.symbol])
      .filter(r => minIvrNum == null
        || !Number.isFinite(minIvrNum)
        || (r.ivr != null && r.ivr >= minIvrNum))
      .slice(0, cap);
    const watchlist = picked.map(r => r.symbol);
    const payloads_by_symbol = {};
    const warningsPerSymbol = {};
    picked.forEach(r => {
      const enriched = {
        ...r,
        __snapshot_id: snapshot.snapshot_id,
        __snapshot_as_of: snapshot.as_of,
      };
      const { payload, warnings } = buildPerSymbolPayload(
        enriched,
        wesSummary.bySymbol[r.symbol],
        wesMeta,
      );
      payloads_by_symbol[r.symbol] = payload;
      warningsPerSymbol[r.symbol] = warnings;
    });
    return { envelope: { watchlist, payloads_by_symbol },
             warningsPerSymbol, picked };
  }

  async function runScan() {
    setErrorMsg(null);
    if (!snapshot) { setErrorMsg("Build a fresh market snapshot first."); return; }
    const { envelope, warningsPerSymbol, picked } = buildEnvelope();
    if (picked.length === 0) { setErrorMsg("Select at least one snapshot symbol."); return; }
    setPerSymbolWarnings(warningsPerSymbol);
    setLastRequest(envelope);
    setLoading(true);
    try {
      const r = await axios.post(`${API}${IRON_CONDOR_ENDPOINT}`, envelope);
      setResult(r.data);
      setLastScannedAt(new Date().toISOString());
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? detail.map(d => d.msg || JSON.stringify(d)).join("; ")
          : (e?.message || "Network error");
      setErrorMsg(msg);
      setResult(null);
    } finally {
      setLoading(false);
    }
  }

  function clearResults() {
    setResult(null); setErrorMsg(null);
    setLastRequest(null); setLastScannedAt(null);
    setPerSymbolWarnings({});
  }

  return (
    <div
      data-testid={`${TID}-panel`}
      className="mb-5 rounded-sm border border-[#FFB800]/30 bg-[#0A0A0E]"
    >
      <button
        type="button"
        data-testid={`${TID}-toggle`}
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#121216]"
      >
        <Search size={16} className="text-[#FFB800]" />
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#E0E0E0]">
          Iron Condor Controlled Auto-Scan
        </span>
        <span className="text-[9px] font-mono uppercase tracking-[0.12em] text-[#5F5F66]">
          · phase 3f · iron condor only · manual-run · no execution
        </span>
        <span className="ml-auto text-[#5F5F66]">
          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </span>
      </button>

      {open && (
        <div className="border-t border-[#26262B]">
          <SnapshotScanKit
            snapshot={snapshot}
            rows={rows}
            staleSecondsCfg={STALE_SECONDS}
            ageSeconds={ageSeconds}
            selected={selected}
            onToggleSymbol={toggleSymbol}
            onSelectAll={selectAll}
            onClearAll={clearAll}
            chainAvailability={chainAvailability}
            maxSymbols={maxSymbols} setMaxSymbols={setMaxSymbols}
            minIvr={minIvr} setMinIvr={setMinIvr}
            loading={loading} onRun={runScan} onClear={clearResults}
            errorMsg={errorMsg}
            runLabel="Run Iron Condor Auto-Scan"
            lastScannedAt={lastScannedAt}
            testidPrefix={TID}
            safetyLabels={SAFETY_LABELS}
          />

          {snapshot && (
            <WesProvenanceStrip
              moduleId="iron-condor"
              wesSummary={wesSummary}
            />
          )}

          {snapshot && !loading && !result && !errorMsg && (
            <div
              data-testid={`${TID}-empty`}
              className="px-4 py-3 text-[10px] font-mono text-[#5F5F66]"
            >
              No Iron Condor auto-scan run yet. Pick snapshot
              symbols and click Run Iron Condor Auto-Scan.
            </div>
          )}

          {snapshot && result && !loading && !errorMsg && (
            <IronCondorResult
              result={result} lastRequest={lastRequest}
              perSymbolWarnings={perSymbolWarnings} snapshot={snapshot}
            />
          )}
        </div>
      )}
    </div>
  );
}

function IronCondorResult({ result, lastRequest, perSymbolWarnings, snapshot }) {
  const counts = result.counts || {};
  return (
    <div data-testid={`${TID}-result`} className="px-4 py-3">
      <div className="flex items-center gap-2 flex-wrap mb-3 text-[10px] font-mono">
        <CheckCircle2 size={11} className="text-[#00FF88]" />
        <span data-testid={`${TID}-result-version`}
              className="text-[#8A8A93] uppercase tracking-[0.12em]">
          {result.scanner_version}
        </span>
        <span data-testid={`${TID}-result-module-id`}
              className="px-2 py-0.5 rounded-sm border border-[#FFB800]/40 bg-[#FFB800]/10 text-[9px] uppercase tracking-[0.14em] text-[#FFB800]">
          {result.module_id}
        </span>
        <span data-testid={`${TID}-result-total`} className="text-[#E0E0E0]">
          {result.total_symbols} symbols scanned
        </span>
        <span className="ml-auto inline-flex items-center gap-3">
          {["enter", "watch", "blocked", "no_trade", "error"].map(k => (
            <span key={k} data-testid={`${TID}-result-count-${k}`}
                  className="flex items-center gap-1 text-[#8A8A93]">
              <span className="uppercase tracking-[0.12em]">{k}</span>
              <span className="font-bold text-[#E0E0E0]">{counts[k] ?? 0}</span>
            </span>
          ))}
        </span>
      </div>

      {Array.isArray(result.warnings) && result.warnings.length > 0 && (
        <div data-testid={`${TID}-result-warnings`}
             className="mb-3 text-[10px] font-mono text-[#FFB800]">
          {result.warnings.map((w, i) => (
            <div key={i} className="flex items-start gap-1">
              <AlertTriangle size={10} className="mt-0.5 shrink-0" />{w}
            </div>
          ))}
        </div>
      )}

      <ul className="space-y-2">
        {(result.results || []).map(rec => {
          const sym = rec.symbol;
          const localWarnings = perSymbolWarnings[sym] || [];
          const merged = {
            ...rec,
            warnings: [
              ...(Array.isArray(rec.warnings) ? rec.warnings : []),
              ...localWarnings,
            ],
          };
          const rowSnap = (snapshot?.rows || []).find(r => r && r.symbol === sym);
          const rowAge = ageSeconds(snapshot?.as_of);
          return (
            <StrategyResultCard
              key={sym} rec={merged} moduleLabel="IRON_CONDOR"
              accent={{
                border: "border-[#FFB800]/30",
                glow:   "bg-[#FFB800]/5",
                text:   "text-[#FFB800]",
              }}
              testidPrefix={`${TID}-result-row`}
            >
              <div
                data-testid={`${TID}-result-row-snapshot-${sym}`}
                className="text-[9px] font-mono uppercase tracking-[0.12em] text-[#5F5F66]"
              >
                snapshot {snapshot?.snapshot_id?.slice(0, 8) || "—"} ·
                {" "}{rowAge != null ? `${rowAge}s ago` : "—"} ·
                {" "}SRC {rowSnap?.source || "—"} ·
                {" "}EXP {rowSnap?.chosen_expiration || "—"}
              </div>
              <ScannerSpreadMetrics
                rec={merged} module="IRON_CONDOR"
                testidPrefix={`${TID}-result-row`}
              />
            </StrategyResultCard>
          );
        })}
      </ul>

      <details
        data-testid={`${TID}-result-request-json`}
        className="mt-3 border border-[#26262B] rounded-sm"
      >
        <summary className="cursor-pointer px-2 py-1 text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93] hover:text-[#E0E0E0]">
          Request payload used (read-only)
        </summary>
        <pre className="mt-1 text-[10px] font-mono text-[#E0E0E0] whitespace-pre-wrap break-words bg-[#08080A] p-2 max-h-64 overflow-auto">
          {JSON.stringify(lastRequest, null, 2)}
        </pre>
      </details>
    </div>
  );
}
